import { createEffect, createMemo, createSignal, type JSX, on, Show } from 'solid-js';
import { t } from '../i18n';
import { type BoxActions, createBoxActions } from '../lib/box-actions';
import type { BoxDetail } from '../lib/box-lifecycle';
import { drainAction, type PanelTarget } from '../lib/fleet-machine-panel';
import type { UtilBox } from '../lib/fleetUtilization';
import { boxJobs } from '../lib/machine-jobs';
import type { MachineRemove, RemoveState } from '../lib/machine-remove';
import { type OrgMemberRow, useOrgMembers } from '../lib/org-members';
import { useMe } from '../lib/session';
import { BoxDrainPanel } from './BoxDrainPanel';
import { BoxOwnerForm } from './BoxOwnerForm';
import { BoxPromptForm } from './BoxPromptForm';
import { MachineRemoveConfirm } from './MachineRemoveConfirm';
import { Skeleton } from './Skeleton';
import { Button } from './ui/Button';

// The details panel's Actions (#6563, prototype `panel()` Actions), an admin's on this
// org's own machine only (owner rulings G21, G23; `MachinePanelAccess` says why not to
// everyone else): Stop or Start taking new work, Instructions (`BoxPromptForm`), Who it
// works for (`BoxOwnerForm`, owner ruling G13) and Remove machine (`createMachineRemove`,
// G11). "Jobs at once" is not here: #6740 owns that row and its route.
//
// Instructions and Who it works for read the point read (`GET /v1/runners/:id`), the only
// place `system_prompt` and `owner_id` live; Stop and Remove read the utilization row, so
// the Remove confirm exists the moment the panel opens at it.

type DetailRead = {
  readonly data: BoxDetail | undefined;
  readonly isLoading: boolean;
};

type OpenAction = 'instructions' | 'owner';

type MachineActionsProps = {
  readonly box: UtilBox;
  readonly detail: DetailRead;
  readonly token: string | null;
  readonly target: PanelTarget;
  readonly remove: MachineRemove;
  readonly onWritten: () => void;
};

type ActionFormProps = {
  readonly open: () => OpenAction | null;
  readonly onToggle: (which: OpenAction) => void;
  readonly onClose: () => void;
};

/** Instructions: the machine's briefing (`BoxPromptForm`). */
function InstructionsActionBlock(
  props: ActionFormProps & {
    readonly actions: BoxActions;
    readonly detailOr: (render: (box: () => BoxDetail) => JSX.Element) => JSX.Element;
  },
): JSX.Element {
  return (
    <div class="machine-panel__action-block">
      <div class="machine-panel__action">
        <Button
          variant="outline"
          aria-expanded={props.open() === 'instructions'}
          onClick={() => props.onToggle('instructions')}
        >
          {t('fleet.panel.instructions')}
        </Button>
        <span class="machine-panel__action-desc">{t('fleet.panel.instructions_desc')}</span>
      </div>
      <Show when={props.open() === 'instructions'}>
        {props.detailOr((box) => (
          <BoxPromptForm
            boxId={box().id}
            preview={box().system_prompt}
            pending={props.actions.pending() === 'prompt'}
            error={props.actions.errorFor('prompt')}
            onSave={props.actions.savePrompt}
            onClear={props.actions.clearPrompt}
          />
        ))}
      </Show>
    </div>
  );
}

/** Who it works for: the machine's ownership (`BoxOwnerForm`, owner ruling G13). */
function OwnerActionBlock(
  props: ActionFormProps & {
    readonly actions: BoxActions;
    readonly orgOwnerId: () => string | null;
    readonly orgLabel: () => string;
    readonly members: () => readonly OrgMemberRow[];
    readonly detailOr: (render: (box: () => BoxDetail) => JSX.Element) => JSX.Element;
  },
): JSX.Element {
  return (
    <div class="machine-panel__action-block">
      <div class="machine-panel__action">
        <Button
          variant="outline"
          aria-expanded={props.open() === 'owner'}
          onClick={() => props.onToggle('owner')}
        >
          {t('fleet.panel.works_for')}
        </Button>
        <span class="machine-panel__action-desc">{t('fleet.panel.works_for_desc')}</span>
      </div>
      <Show when={props.open() === 'owner'}>
        {props.detailOr((box) => (
          <BoxOwnerForm
            boxId={box().id}
            ownerKind={box().owner_kind}
            ownerId={box().owner_id}
            orgOwnerId={props.orgOwnerId()}
            orgLabel={props.orgLabel()}
            members={props.members()}
            pending={props.actions.pending() === 'owner'}
            error={props.actions.errorFor('owner')}
            onSave={props.actions.saveOwner}
          />
        ))}
      </Show>
    </div>
  );
}

/** Remove machine: the one destructive act, behind its inline confirm (`createMachineRemove`, G11). */
function RemoveActionBlock(props: {
  readonly box: UtilBox;
  readonly remove: MachineRemove;
  readonly confirming: () => RemoveState | null;
}): JSX.Element {
  return (
    <div class="machine-panel__action-block">
      <div class="machine-panel__action">
        <Button
          variant="destructive"
          aria-expanded={props.confirming() !== null}
          onClick={() => props.remove.ask(props.box)}
        >
          {t('fleet.machines.menu_remove')}
        </Button>
        <span class="machine-panel__action-desc">{t('fleet.panel.remove_desc')}</span>
      </div>
      <Show when={props.confirming()}>
        {(s) => (
          <MachineRemoveConfirm
            name={s().name}
            hasTask={boxJobs(props.box).length > 0 || props.box.verdict.status === 'working'}
            busy={props.remove.busy()}
            error={s().error}
            onConfirm={props.remove.confirm}
            onCancel={props.remove.cancel}
          />
        )}
      </Show>
    </div>
  );
}

export function MachineActions(props: MachineActionsProps): JSX.Element {
  const [open, setOpen] = createSignal<OpenAction | null>(null);
  const onToggle = (which: OpenAction): void => {
    setOpen((now) => (now === which ? null : which));
  };
  const onClose = (): void => {
    setOpen(null);
  };
  const members = useOrgMembers(props.token, () => open() === 'owner');
  const me = useMe(props.token);
  const actions = createBoxActions({
    boxId: () => props.box.id,
    name: () => props.box.display_name,
    token: props.token,
    onWritten: () => props.onWritten(),
  });

  // Opening another machine, or opening this one at Instructions, starts from that. Keyed
  // on the id through a memo, never on the row: every re-read (the poll, or the one a write
  // asks for) hands over a new row object for the same machine, and resetting on it would
  // wipe the notice that write just said and close any open confirm.
  const boxId = createMemo(() => props.box.id);
  createEffect(
    on([boxId, () => props.target], ([, target]) => {
      actions.reset();
      setOpen(target === 'instructions' ? 'instructions' : null);
    }),
  );

  const drain = () => drainAction(props.box);
  const confirming = () => {
    const s = props.remove.state();
    return s !== null && s.id === props.box.id && s.phase === 'confirming' ? s : null;
  };
  const orgOwnerId = (): string | null => me.data?.active_account_id ?? me.data?.id ?? null;
  const orgLabel = (): string => me.data?.github_login ?? '';

  // The ACCESSOR goes through, never `box()`: this Show is not keyed, so its child runs
  // once, and a value read here would freeze the forms on the first answer while every
  // write's re-read went unseen. Each prop reads `box()` where the form reads it.
  const detailOr = (render: (box: () => BoxDetail) => JSX.Element): JSX.Element => (
    <Show when={props.detail.data} fallback={<Skeleton height="6rem" />}>
      {(box) => render(box)}
    </Show>
  );

  return (
    <section class="machine-panel__section" aria-labelledby="machine-panel-actions">
      <h3 id="machine-panel-actions" class="machine-panel__h3">
        {t('fleet.panel.actions')}
      </h3>

      <Show when={actions.notice()}>
        {(msg) => (
          <p class="field__desc" role="status">
            {msg()}
          </p>
        )}
      </Show>

      <Show when={drain()}>
        <BoxDrainPanel
          boxId={props.box.id}
          name={props.box.display_name}
          drainedIso={props.box.drained_iso ?? null}
          confirming={actions.confirmingDrain()}
          busy={actions.pending() !== null}
          error={actions.errorFor('drain') ?? actions.errorFor('undrain')}
          onAsk={actions.askDrain}
          onCancel={actions.cancelDrain}
          onConfirm={actions.confirmDrain}
          onUndrain={actions.undrain}
        />
      </Show>
      <InstructionsActionBlock
        open={open}
        onToggle={onToggle}
        onClose={onClose}
        actions={actions}
        detailOr={detailOr}
      />
      <OwnerActionBlock
        open={open}
        onToggle={onToggle}
        onClose={onClose}
        actions={actions}
        orgOwnerId={orgOwnerId}
        orgLabel={orgLabel}
        members={() => members.data ?? []}
        detailOr={detailOr}
      />
      <RemoveActionBlock box={props.box} remove={props.remove} confirming={confirming} />
    </section>
  );
}
